// Camada 1 · MARKDOWN em texto, sem biblioteca.
//
// MIGRADO de `~/Documents/prontidaovoo/src/componentes/Markdown.tsx`, copia integral.
//
// POR QUE ELE MORA EM `packages/ui` E NAO NO PRODUTO: tres telas ja precisam dele nesta
// plataforma (a ajuda, a resposta de IA e a ficha de norma), e um markdown por produto
// divergiria no primeiro ajuste de espacamento. A unica dependencia dele e o React.
//
// POR QUE SEM BIBLIOTECA, e a razao esta escrita abaixo pela origem: o subconjunto que estas
// telas usam e pequeno e conhecido, e uma biblioteca de markdown traz um parser inteiro para o
// pacote, com o risco de HTML injetado que ela precisa tratar.
//
import type { ReactNode } from 'react'

// Renderizador mínimo de markdown para as fichas de normas. Sem dependência nova, de propósito:
// a ficha é sempre gerada por este projeto (seed ou tela de edição futura), então cobre só o que
// o conteúdo real usa: título de nível 2 (##), parágrafo, lista com "-", negrito (**texto**) e
// link ([texto](url)). Qualquer coisa fora disso é impressa como texto puro, nunca descartada.

// Marca [verificar] destaca trecho que não foi confirmado contra a fonte oficial (ver
// supabase/seeds/normas.sql). Ela precisa ficar visível em text-atencao mesmo dentro de negrito
// ou de item de lista, então a função roda por cima de qualquer texto já quebrado em partes.
function destacarVerificar(texto: string, chavePrefixo: string): ReactNode[] {
  const partes = texto.split(/(\[verificar\])/g)
  return partes.map((parte, indice) =>
    parte === '[verificar]' ? (
      <span key={`${chavePrefixo}-verificar-${indice}`} className="font-medium text-atencao">
        {parte}
      </span>
    ) : (
      <span key={`${chavePrefixo}-texto-${indice}`}>{parte}</span>
    ),
  )
}

// Quebra negrito (**texto**) e link ([texto](url)) na mesma passada, porque um pode conter o
// outro na prática ("**[RBAC 135](url)**") e duas passadas separadas embaralhariam a ordem.
function renderizarLinhaInline(texto: string, chavePrefixo: string): ReactNode[] {
  const regex = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)/g
  const nos: ReactNode[] = []
  let ultimoIndice = 0
  let contador = 0
  let resultado: RegExpExecArray | null

  while ((resultado = regex.exec(texto)) !== null) {
    if (resultado.index > ultimoIndice) {
      const trecho = texto.slice(ultimoIndice, resultado.index)
      nos.push(...destacarVerificar(trecho, `${chavePrefixo}-${contador++}`))
    }
    if (resultado[1] !== undefined) {
      // Negrito.
      nos.push(
        <strong key={`${chavePrefixo}-b-${contador++}`}>
          {destacarVerificar(resultado[1], `${chavePrefixo}-b-${contador}`)}
        </strong>,
      )
    } else {
      // Link. Abre em nova aba: a ficha resume e a fonte oficial é o destino, então sair do
      // sistema não pode custar o lugar onde a pessoa estava lendo.
      nos.push(
        <a
          key={`${chavePrefixo}-a-${contador++}`}
          href={resultado[3]}
          target="_blank"
          rel="noreferrer"
          // `text-acento` NAO EXISTE, medido no navegador em 09/09/2026: a cor caia para a
          // herdada e o link ficava com a cor do paragrafo. O sublinhado salvava a
          // affordance, entao o defeito nunca chamou atencao, e o link continuava sem a cor
          // da marca em toda a plataforma.
          className="text-marca underline underline-offset-2"
        >
          {resultado[2]}
        </a>,
      )
    }
    ultimoIndice = resultado.index + resultado[0].length
  }
  if (ultimoIndice < texto.length) {
    nos.push(...destacarVerificar(texto.slice(ultimoIndice), `${chavePrefixo}-${contador++}`))
  }
  return nos
}

/**
 * Um bloco de linhas em pipes vira <table> de verdade.
 *
 * POR QUE UMA FUNCAO SEPARADA E NAO MAIS UM RAMO DO LACO: a tabela e o unico bloco deste
 * subconjunto que precisa olhar mais de UMA linha para decidir o que fazer com a primeira,
 * porque e a SEGUNDA linha que diz se ela era cabecalho. Resolver isso dentro do laco
 * obrigaria o laco a espiar a linha seguinte, e ele passaria a decidir por duas.
 */
function montarTabela(linhas: string[], chave: string): ReactNode {
  // A linha separadora e a que so tem traco, dois pontos e barra. So vale como separador na
  // SEGUNDA posicao: no meio da tabela ela e uma linha de dados que por acaso comeca com traco.
  const separador = /^\|[\s:|-]*-{3,}[\s:|-]*\|$/
  const temCabecalho = linhas.length > 1 && separador.test(linhas[1] ?? '')

  // As bordas externas somem antes de cortar, senao a divisao devolve uma celula vazia em
  // cada ponta e a tabela nasce com uma coluna fantasma de cada lado.
  const celulas = (linha: string) =>
    linha
      .slice(1, -1)
      .split('|')
      .map((c) => c.trim())

  const cabecalho = temCabecalho ? celulas(linhas[0] ?? '') : null
  const corpo = (temCabecalho ? linhas.slice(2) : linhas).map(celulas)

  return (
    // A ROLAGEM E DO CONTEINER, e nunca da pagina. Uma tabela de tres colunas nao cabe em
    // 390px, e pagina que rola de lado quebra a leitura de tudo o que esta em volta, nao so
    // da tabela. E a mesma regra que vale para bloco de codigo e diagrama.
    <div key={chave} className="overflow-x-auto">
      <table className="w-full border-collapse text-sm text-texto">
        {cabecalho !== null && (
          <thead>
            <tr>
              {cabecalho.map((c, indice) => (
                <th
                  key={`${chave}-th-${indice}`}
                  className="border border-borda px-2 py-1 text-left font-semibold"
                >
                  {renderizarLinhaInline(c, `${chave}-th-${indice}`)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {corpo.map((linha, i) => (
            <tr key={`${chave}-tr-${i}`}>
              {linha.map((c, j) => (
                <td
                  key={`${chave}-td-${i}-${j}`}
                  className="border border-borda px-2 py-1 align-top"
                >
                  {renderizarLinhaInline(c, `${chave}-td-${i}-${j}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function Markdown({ texto }: { texto: string }) {
  const linhas = texto.split('\n')
  const blocos: ReactNode[] = []
  // Acumula itens de lista consecutivos para fechar uma única <ul>, em vez de uma lista por item.
  let itensListaAtual: string[] = []
  // O MESMO acumulo para a lista NUMERADA, em vetor proprio: um vetor so obrigaria a guardar
  // o tipo de cada item ao lado dele, e uma lista mista nao existe em markdown.
  let itensNumeradosAtual: string[] = []
  // E para a tabela, que so pode ser montada quando a ultima linha dela ja passou.
  let linhasTabelaAtual: string[] = []

  function fecharLista(chave: string) {
    if (itensListaAtual.length === 0) return
    blocos.push(
      <ul key={chave} className="ml-5 list-disc space-y-1 text-sm text-texto">
        {itensListaAtual.map((item, indice) => (
          <li key={`${chave}-${indice}`}>{renderizarLinhaInline(item, `${chave}-${indice}`)}</li>
        ))}
      </ul>,
    )
    itensListaAtual = []
  }

  function fecharNumerada(chave: string) {
    if (itensNumeradosAtual.length === 0) return
    // <ol> E NAO <ul> COM NUMERO NO TEXTO: quem usa leitor de tela ouve "lista de 4 itens,
    // item 1 de 4", e essa contagem e justamente o que um artigo de passo a passo promete.
    blocos.push(
      <ol key={chave} className="ml-5 list-decimal space-y-1 text-sm text-texto">
        {itensNumeradosAtual.map((item, indice) => (
          <li key={`${chave}-n-${indice}`}>
            {renderizarLinhaInline(item, `${chave}-n-${indice}`)}
          </li>
        ))}
      </ol>,
    )
    itensNumeradosAtual = []
  }

  function fecharTabela(chave: string) {
    if (linhasTabelaAtual.length === 0) return
    blocos.push(montarTabela(linhasTabelaAtual, chave))
    linhasTabelaAtual = []
  }

  /** Fecha TUDO que estava aberto. Existe para nenhum ramo esquecer um dos tres acumuladores. */
  function fecharBlocos(chave: string) {
    fecharLista(chave)
    fecharNumerada(chave)
    fecharTabela(chave)
  }

  linhas.forEach((linhaBruta, indice) => {
    const linha = linhaBruta.trim()
    const chave = `bloco-${indice}`

    if (linha === '') {
      // Linha em branco fecha uma lista em andamento; parágrafo vazio não gera nó nenhum.
      fecharBlocos(chave)
      return
    }

    // A TABELA VEM ANTES DOS DEMAIS TESTES porque uma celula pode comecar com traco ou com
    // numero, e ai a linha inteira seria confundida com item de lista.
    if (linha.startsWith('|') && linha.endsWith('|') && linha.length > 1) {
      fecharLista(chave)
      fecharNumerada(chave)
      linhasTabelaAtual.push(linha)
      return
    }

    if (linha.startsWith('## ')) {
      fecharBlocos(chave)
      blocos.push(
        <h2 key={chave} className="mt-4 text-sm font-semibold text-texto first:mt-0">
          {renderizarLinhaInline(linha.slice(3), chave)}
        </h2>,
      )
      return
    }

    /**
     * O TITULO DE NIVEL 1 CAI NO MESMO <h2> do nivel 2, de proposito.
     *
     * O conteudo da base abre com `# <titulo do artigo>` e a tela ja desenhou esse titulo
     * acima do corpo, entao aqui ele e repeticao e nao um nivel novo. Promove-lo a <h1>
     * criaria DOIS <h1> na mesma pagina, e rebaixar o `##` para <h3> mudaria a tela de
     * Normas do VOE e a Central, que nao foram pedidas. Igualar e a mudanca que tira o
     * caractere da tela sem mexer em quem ja depende do componente.
     */
    if (linha.startsWith('# ')) {
      fecharBlocos(chave)
      blocos.push(
        <h2 key={chave} className="mt-4 text-sm font-semibold text-texto first:mt-0">
          {renderizarLinhaInline(linha.slice(2), chave)}
        </h2>,
      )
      return
    }

    if (linha.startsWith('- ')) {
      fecharNumerada(chave)
      fecharTabela(chave)
      itensListaAtual.push(linha.slice(2))
      return
    }

    // O ponto e o espaco sao exigidos juntos: sem eles, "2026. o ano" viraria item de lista.
    const numerada = /^(\d+)\.\s+(.*)$/.exec(linha)
    if (numerada !== null) {
      fecharLista(chave)
      fecharTabela(chave)
      itensNumeradosAtual.push(numerada[2] ?? '')
      return
    }

    fecharBlocos(chave)
    blocos.push(
      <p key={chave} className="text-sm leading-relaxed text-texto">
        {renderizarLinhaInline(linha, chave)}
      </p>,
    )
  })
  fecharBlocos('bloco-final')

  return <div className="space-y-2">{blocos}</div>
}
